/**
 * Render-only preview of a generated (not yet persisted) wall skeleton — used
 * by the WallCreateTool to show the sketch that accepting the current room
 * selection would produce.
 *
 * Renders the SAME flat 2D wall skinning the real sketch shows (the SDF wall
 * partition flattened to one footprint via Manifold and triangulated, plus the
 * footprint outline) so the preview looks exactly like the committed result —
 * just built from standalone helpers instead of a `PointoramaWallSketch`. The
 * scene class is deliberately avoided: it registers viewer listeners without a
 * matching dispose, writes global OPENING properties from
 * `orphanOpeningsForRemovedEdges` on every `setData` (with deterministic
 * `node_N`/`edge_N` ids that collide across floors), and auto-parents its
 * skinning group into the interactive floorplan scene. A preview must render
 * and vanish without any of those side effects.
 */

import * as THREE from 'three';
import earcut from 'earcut';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Viewer } from '../../viewer/viewer';
import { FloorSkeleton } from '../SkeletonProcessingPipeline';
import type { SkeletonNodeData, SkeletonEdgeData } from '@pointorama/potree-shared';
import { computeSdfWallRegions } from './sdfPartition';
import type { WallRegion } from './stitchedWallMesh';
import { regionsToFootprint2D, isManifoldReady, onManifoldReady } from '../manifoldCsg';
import { DefaultColors } from '../../defines';

// Match the real 2D skin (PointoramaWallSketch defaults) so the preview reads
// as "this is what you'll get", just not yet committed.
const FILL_COLOR = '#808080';
const FILL_OPACITY = 0.4;
const OUTLINE_COLOR = 0x000000;
const OUTLINE_WIDTH = 3;
/** Lift above the floor plane so the skin never z-fights the outline preview. */
const PREVIEW_Z_LIFT = 0.02;
/** Edge centerline width — matches the sketch's LineMaterial linewidth. */
const CENTERLINE_WIDTH = 1;
/** On-screen node dot size — approximates the sketch's SphereMarkers at the
 * resting (non-editor) 0.6 sphere scale. */
const NODE_DOT_PX = 10;

export interface WallSketchPreviewOptions {
  /** Render only OUTER-walltype edges (the building outline). */
  outerWallsOnly?: boolean;
}

export class WallSketchPreviewRenderer {
  private readonly viewer: Viewer;
  private group: THREE.Group | null = null;
  private readonly disposables: { dispose: () => void }[] = [];
  /** Last input, kept so the preview can re-render (with the clean Manifold
   * footprint) once the WASM module finishes loading mid-preview. */
  private lastInput: { skeleton: FloorSkeleton; floorHeight: number; opts?: WallSketchPreviewOptions } | null = null;
  private manifoldReadyUnsub: (() => void) | null = null;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
  }

  /** Replace the rendered preview with `skeleton`. Full rebuild — geometry
   * counts are small and the caller debounces updates.
   *
   * `opts.outerWallsOnly` renders only the OUTER-walltype edges (the
   * building outline) — used on Measured-source floors, whose representation
   * shows the outline but not the interior skeleton. The skeleton itself is
   * untouched: the caller still caches/commits the FULL generated data. */
  setSkeleton(skeleton: FloorSkeleton, floorHeight: number, opts?: WallSketchPreviewOptions): void {
    this.clear();
    this.lastInput = { skeleton, floorHeight, opts };

    const nodes = Array.from(skeleton.nodes.values());
    if (nodes.length === 0) return;

    // Adapt the pipeline's node/edge shapes to the persisted-data shapes the
    // partition helpers expect (they only differ by the `name` field).
    const nodeMap = new Map<string, SkeletonNodeData>();
    for (const n of nodes) {
      nodeMap.set(n.id, {
        id: n.id,
        name: n.id,
        position: { x: n.position.x, y: n.position.y, z: n.position.z },
        roomIds: n.roomIds,
        edgeIds: n.edgeIds,
        metadata: n.metadata,
      });
    }
    let edges: SkeletonEdgeData[] = Array.from(skeleton.edges.values()).map((e) => ({
      id: e.id,
      name: e.id,
      startNodeId: e.startNodeId,
      endNodeId: e.endNodeId,
      roomIds: e.roomIds,
      leftSize: e.leftSize ?? 0.1,
      rightSize: e.rightSize ?? 0.1,
      wallType: e.wallType ?? 'INNER',
      metadata: e.metadata,
    }));
    if (opts?.outerWallsOnly) {
      edges = edges.filter((e) => e.wallType === 'OUTER');
    }
    if (edges.length === 0) return;

    // Same partition the real skinning uses. Heights don't matter for the
    // flat 2D footprint, so the default bounds resolver suffices.
    const regions = computeSdfWallRegions(edges, nodeMap, floorHeight);
    if (regions.length === 0) return;

    // Localize all geometry to the first node so float32 vertex buffers stay
    // precise at UTM-scale world coordinates (same pattern as the scene
    // skeleton's skinning group).
    const ref = nodes[0].position;
    const group = new THREE.Group();
    group.name = 'wall-sketch-preview';
    group.position.set(ref.x, ref.y, floorHeight + PREVIEW_Z_LIFT);

    const fillVertices: number[] = [];
    const outlinePositions: number[] = [];
    const pushTriangles = (flat: number[], holeIdx: number[] | null) => {
      const tris = earcut(flat, holeIdx && holeIdx.length > 0 ? holeIdx : null, 2);
      for (let i = 0; i < tris.length; i += 3) {
        for (const v of [tris[i], tris[i + 1], tris[i + 2]]) {
          fillVertices.push(flat[v * 2] - ref.x, flat[v * 2 + 1] - ref.y, 0);
        }
      }
    };

    const footprint = isManifoldReady() ? this.computeFootprint(regions) : null;
    if (footprint) {
      for (const poly of footprint) {
        const flat: number[] = [];
        const holeIdx: number[] = [];
        for (const p of poly.outer) flat.push(p[0], p[1]);
        for (const hole of poly.holes) {
          holeIdx.push(flat.length / 2);
          for (const p of hole) flat.push(p[0], p[1]);
        }
        pushTriangles(flat, holeIdx);
        for (const ring of [poly.outer, ...poly.holes]) {
          for (let i = 0; i < ring.length; i++) {
            const a = ring[i];
            const b = ring[(i + 1) % ring.length];
            outlinePositions.push(a[0] - ref.x, a[1] - ref.y, 0, b[0] - ref.x, b[1] - ref.y, 0);
          }
        }
      }
    } else {
      // WASM not ready: per-region fill (regions overlap node cells — invisible
      // at this opacity), no outline; re-render clean once Manifold loads.
      for (const region of regions) {
        if (region.ring.length < 3) continue;
        pushTriangles(
          region.ring.flatMap((p) => [p.x, p.y]),
          null,
        );
      }
      this.manifoldReadyUnsub?.();
      this.manifoldReadyUnsub = onManifoldReady(() => {
        this.manifoldReadyUnsub = null;
        if (this.lastInput) this.setSkeleton(this.lastInput.skeleton, this.lastInput.floorHeight, this.lastInput.opts);
      });
    }

    if (fillVertices.length > 0) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(fillVertices), 3));
      const material = new THREE.MeshBasicMaterial({
        color: FILL_COLOR,
        side: THREE.DoubleSide,
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: FILL_OPACITY,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.renderOrder = 98;
      mesh.raycast = () => undefined;
      group.add(mesh);
      this.disposables.push(geometry, material);
    }

    const resolution = new THREE.Vector2(this.viewer.renderer.domElement.clientWidth, this.viewer.renderer.domElement.clientHeight);
    if (outlinePositions.length > 0) {
      group.add(this.buildSegments(outlinePositions, resolution, OUTLINE_COLOR, OUTLINE_WIDTH, 0.9, 99));
    }

    // Edge centerlines + node dots — same reading as the regular wall sketch
    // (its centerline LineSegments2 + SphereMarkers), drawn above the skin.
    const skeletonColor = DefaultColors.OBJECTS_THREE;
    const centerlinePositions: number[] = [];
    for (const edge of edges) {
      const a = nodeMap.get(edge.startNodeId)?.position;
      const b = nodeMap.get(edge.endNodeId)?.position;
      if (!a || !b) continue;
      centerlinePositions.push(a.x - ref.x, a.y - ref.y, 0, b.x - ref.x, b.y - ref.y, 0);
    }
    if (centerlinePositions.length > 0) {
      group.add(this.buildSegments(centerlinePositions, resolution, skeletonColor.getHex(), CENTERLINE_WIDTH, 1, 100));
    }

    const nodePositions = new Float32Array(nodes.length * 3);
    nodes.forEach((node, i) => {
      nodePositions[i * 3] = node.position.x - ref.x;
      nodePositions[i * 3 + 1] = node.position.y - ref.y;
      nodePositions[i * 3 + 2] = 0;
    });
    const pointsGeometry = new THREE.BufferGeometry();
    pointsGeometry.setAttribute('position', new THREE.BufferAttribute(nodePositions, 3));
    const pointsMaterial = new THREE.PointsMaterial({
      color: skeletonColor,
      size: NODE_DOT_PX,
      sizeAttenuation: false,
      map: circleSprite(),
      alphaTest: 0.5,
      transparent: true,
      depthTest: false,
    });
    const points = new THREE.Points(pointsGeometry, pointsMaterial);
    points.renderOrder = 101;
    points.raycast = () => undefined;
    group.add(points);
    this.disposables.push(pointsGeometry, pointsMaterial);

    this.group = group;
    this.viewer.measurementScene.add(group);
  }

  private buildSegments(positions: number[], resolution: THREE.Vector2, color: number, linewidth: number, opacity: number, renderOrder: number): LineSegments2 {
    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(positions);
    const material = new LineMaterial({
      color,
      linewidth,
      resolution,
      depthTest: false,
      transparent: true,
      opacity,
    });
    const line = new LineSegments2(geometry, material);
    line.computeLineDistances();
    line.renderOrder = renderOrder;
    line.raycast = () => undefined;
    this.disposables.push(geometry, material);
    return line;
  }

  /** The regions' unioned 2D footprint via Manifold, in absolute coords. */
  private computeFootprint(regions: WallRegion[]) {
    return regionsToFootprint2D(
      regions.map((r) => ({
        ring: r.ring.map((p) => ({ x: p.x, y: p.y })),
        bottom: r.bottom,
        top: r.top,
        subtract: r.subtract?.map((cell) => cell.map((p) => ({ x: p.x, y: p.y }))),
      })),
    );
  }

  /** Remove the preview from the scene and free its GPU resources. */
  clear(): void {
    this.lastInput = null;
    this.manifoldReadyUnsub?.();
    this.manifoldReadyUnsub = null;
    if (this.group) {
      this.viewer.measurementScene.remove(this.group);
      this.group = null;
    }
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
  }

  dispose(): void {
    this.clear();
  }
}

/** Shared round dot texture for the node points (built once, module-scoped). */
let cachedCircleSprite: THREE.CanvasTexture | null = null;
function circleSprite(): THREE.CanvasTexture {
  if (cachedCircleSprite) return cachedCircleSprite;
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
    ctx.fill();
  }
  cachedCircleSprite = new THREE.CanvasTexture(canvas);
  return cachedCircleSprite;
}
